import React, { FC, Fragment } from 'react';
import { TableBody, TableCell, TableRow } from '@digdir/designsystemet-react';
import { useTranslation } from 'react-i18next';

import {
  AccentedTableRow,
  LeftBorderCell,
  StyledTable
} from '../../../../expertRegistration/json/enhet/upload/fileReview/components/styledComponents';
import { Energiforbruk, EnergyPlan } from '../../../../../types/building';

interface LevertOgVektetEnergiProps {
  totalOppvarmetBRA: number | undefined;
  energiplan: EnergyPlan;
  energiforbruk: Energiforbruk;
}

export const LevertEnergiOgVektetView: FC<LevertOgVektetEnergiProps> = ({
  totalOppvarmetBRA,
  energiplan,
  energiforbruk
}) => {
  const { t } = useTranslation();

  const filteredLevertEnergi = {
    elektrisitet: energiforbruk.elektrisitet,
    olje: energiforbruk.olje,
    gass: energiforbruk.gass,
    biobrensel: energiforbruk.biobrensel,
    fjernvarme: energiforbruk.fjernvarme,
    annenEnergivare: energiforbruk.annen_energivare,
    total: energiforbruk.totalt_levert_energi
  };

  const weightingFactors = energiplan?.energimerke?.inndata?.beregningskjerneInput?.energipolitiskVektetEnergibehov;

  const totalVektetLevertEnergi = energiforbruk.vektet_levert_energi > 0;
  const filteredVektetLevertEnergi = !weightingFactors
    ? { total: energiforbruk.vektet_levert_energi }
    : {
        elektrisitet: energiforbruk.elektrisitet * (weightingFactors.vektingsfaktor_el?.calculatedValue ?? undefined),
        olje: energiforbruk.olje * (weightingFactors.vektingsfaktor_olje?.calculatedValue ?? undefined),
        gass: energiforbruk.gass * (weightingFactors.vektingsfaktor_gass?.calculatedValue ?? undefined),
        biobrensel: energiforbruk.biobrensel * (weightingFactors.vektingsfaktor_bio?.calculatedValue ?? undefined),
        fjernvarme:
          energiforbruk.fjernvarme * (weightingFactors.vektingsfaktor_fjernvarme?.calculatedValue ?? undefined),
        annenEnergivare:
          energiforbruk.annen_energivare * (weightingFactors.vektingsfaktor_annet?.calculatedValue ?? undefined),
        total: energiforbruk.vektet_levert_energi
      };

  return (
    <StyledTable>
      <TableBody>
        <AccentedTableRow $isSecondarySection>
          <TableCell colSpan={totalVektetLevertEnergi ? 5 : 3}>{t('register.summary.header.levertEnergi')}</TableCell>
        </AccentedTableRow>
        <AccentedTableRow $isSecondarySection>
          <TableCell>{t('register.summary.result.energivare')}</TableCell>
          <LeftBorderCell>{t('register.summary.header.levertEnergiPerYear')}</LeftBorderCell>
          <LeftBorderCell>{t('register.summary.header.spesifiktLevertEnergi')}</LeftBorderCell>
          {totalVektetLevertEnergi && (
            <Fragment>
              <LeftBorderCell>{t('register.summary.header.vektetLevertEnergiPerYear')}</LeftBorderCell>
              <LeftBorderCell>{t('register.summary.header.spesifikkVektetLevertEnergi')}</LeftBorderCell>
            </Fragment>
          )}
        </AccentedTableRow>
        <AccentedTableRow>
          <TableCell />
          <LeftBorderCell>{t('expertToolsJSON.summary.suffix.kWh')}</LeftBorderCell>
          <LeftBorderCell>{t('expertToolsJSON.summary.suffix.kWhM2')}</LeftBorderCell>
          {totalVektetLevertEnergi && (
            <Fragment>
              <LeftBorderCell>{t('expertToolsJSON.summary.suffix.kWh')}</LeftBorderCell>
              <LeftBorderCell>{t('expertToolsJSON.summary.suffix.kWhM2')}</LeftBorderCell>
            </Fragment>
          )}
        </AccentedTableRow>
        {Object.entries(filteredLevertEnergi).map(([key, value]) => {
          const vektetValue = (filteredVektetLevertEnergi as any)[key];
          const isTotalRow = key === 'total';

          return isTotalRow ? (
            <AccentedTableRow key={key}>
              <TableCell>{t(`register.summary.result.${key}`)}</TableCell>
              <LeftBorderCell>{value.toFixed(1)}</LeftBorderCell>
              <LeftBorderCell>{totalOppvarmetBRA ? (value / totalOppvarmetBRA).toFixed(2) : '0.00'}</LeftBorderCell>
              {totalVektetLevertEnergi && (
                <Fragment>
                  <TableCell>{vektetValue?.toFixed(1) || '0.0'}</TableCell>
                  <LeftBorderCell>
                    {totalOppvarmetBRA && vektetValue ? (vektetValue / totalOppvarmetBRA).toFixed(2) : '0.00'}
                  </LeftBorderCell>
                </Fragment>
              )}
            </AccentedTableRow>
          ) : (
            value > 0 && (
              <TableRow key={key}>
                <TableCell>{t(`register.summary.result.${key}`)}</TableCell>
                <LeftBorderCell>{value.toFixed(1)}</LeftBorderCell>
                <LeftBorderCell>{totalOppvarmetBRA ? (value / totalOppvarmetBRA).toFixed(2) : '0.00'}</LeftBorderCell>
                {totalVektetLevertEnergi && (
                  <Fragment>
                    <TableCell>{vektetValue?.toFixed(1) || '0.0'}</TableCell>
                    <LeftBorderCell>
                      {totalOppvarmetBRA && vektetValue ? (vektetValue / totalOppvarmetBRA).toFixed(2) : '0.00'}
                    </LeftBorderCell>
                  </Fragment>
                )}
              </TableRow>
            )
          );
        })}
      </TableBody>
    </StyledTable>
  );
};
